/**
 * Production-only consent bridge and verified legacy affiliate integrations.
 * The Stape app owns GTM. Its container owns the CMP and Google tags.
 * This asset does not initialize either or synthesize commerce events.
 */
(() => {
  const { Shopify } = window;
  if (!Shopify || Shopify.shop !== 'lunya.myshopify.com' || Shopify.designMode
      || window.lunyaProductionTrackingInitialized) return;
  window.lunyaProductionTrackingInitialized = true;

  let loadingPrivacy = false;
  let privacyLoadAttempts = 0;
  let privacyRetryTimer = null;
  let updatingConsent = false;
  let lastApplied = '';
  let sawResponse = false;
  let ownedTuneQueue;
  const scriptAttempts = new Map();

  function choice() {
    const bot = window.Cookiebot;
    // Initial defaults are not a visitor's decision. Do not manufacture consent.
    if (!bot || !bot.consent) return null;
    if (bot.hasResponse !== true) {
      // A previously observed response being cleared is a withdrawal, not the
      // initial pending state. Block processing until another choice is made.
      return sawResponse ? { analytics: false, marketing: false, preferences: false } : null;
    }
    const { statistics, marketing, preferences } = bot.consent;
    if ([statistics, marketing, preferences].some((value) => typeof value !== 'boolean')) return null;
    sawResponse = true;
    return { analytics: statistics, marketing, preferences };
  }

  function hasScript(src) {
    return Array.from(document.scripts).some((script) => {
      try {
        const existing = new URL(script.src);
        const target = new URL(src);
        return existing.origin === target.origin && existing.pathname === target.pathname;
      } catch {
        return false;
      }
    });
  }

  function appendScript(src) {
    const attempts = scriptAttempts.get(src) || 0;
    if (hasScript(src) || attempts >= 3) return;
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-cookieconsent', 'marketing');
    // Only remove a failed element that this adapter owns. Loaded/pending and
    // externally owned scripts retain their duplicate guard. Retry on a later
    // consent event, with the same permission gates and a per-page attempt cap.
    script.onerror = () => script.remove();
    scriptAttempts.set(src, attempts + 1);
    document.head.appendChild(script);
  }

  function loadAffiliates() {
    const consent = choice();
    const privacy = Shopify.customerPrivacy;
    try {
      // Shopify's Allowed methods also account for regional settings / GPC.
      if (!consent?.marketing || !privacy?.marketingAllowed()
          || !privacy.saleOfDataAllowed()) return;
      appendScript('https://static.shopmy.us/Affiliates/sms_aff_clicktrack.js');
      const tune = 'https://js.go2sdk.com/v2/tune.js';
      if (!hasScript(tune) && !window.tdl?.invoked) {
        const queue = window.tdl = window.tdl || [];
        ownedTuneQueue = queue;
        queue.invoked = true;
        queue.methods = ['init', 'identify', 'convert'];
        queue.factory = (method) => (...args) => {
          queue.push([method, ...args]);
          return queue;
        };
        queue.methods.forEach((method) => { queue[method] = queue.factory(method); });
        queue.init = (domain) => {
          queue.domain = domain;
          appendScript(tune);
        };
        queue.init('https://aspireiq.go2cloud.org');
        queue.identify();
      } else if (ownedTuneQueue && window.tdl === ownedTuneQueue) {
        // Retain the queued init/identify across transport failures; re-request
        // only our SDK, without identifying twice or touching another owner.
        appendScript(tune);
      }
      appendScript('https://onsite.joinground.com/ground.js');
    } catch {
      // Missing/failed privacy APIs must not start marketing scripts.
    }
  }

  function retryPrivacy() {
    if (privacyRetryTimer !== null || privacyLoadAttempts >= 3) return;
    privacyRetryTimer = window.setTimeout(() => {
      privacyRetryTimer = null;
      synchronize();
    }, privacyLoadAttempts * 1000);
  }

  function synchronize() {
    const consent = choice();
    if (!consent) return;
    const privacy = Shopify.customerPrivacy;
    if (!privacy?.setTrackingConsent) {
      if (loadingPrivacy || privacyRetryTimer !== null || privacyLoadAttempts >= 3
          || typeof Shopify.loadFeatures !== 'function') return;
      loadingPrivacy = true;
      privacyLoadAttempts += 1;
      try {
        Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          loadingPrivacy = false;
          if (!error && Shopify.customerPrivacy?.setTrackingConsent) synchronize();
          else retryPrivacy();
        });
      } catch {
        loadingPrivacy = false;
        retryPrivacy();
      }
      return;
    }
    if (privacyRetryTimer !== null) {
      window.clearTimeout(privacyRetryTimer);
      privacyRetryTimer = null;
    }
    if (updatingConsent) return;
    const key = JSON.stringify(consent);
    if (key === lastApplied) {
      loadAffiliates();
      return;
    }
    try {
      const existing = privacy.currentVisitorConsent();
      if (Object.entries(consent).every(([purpose, allowed]) => existing[purpose] === (allowed ? 'yes' : 'no'))) {
        lastApplied = key;
        loadAffiliates();
        return;
      }
      updatingConsent = true;
      // Sale-of-data is a separate opt-out flow. Never overwrite that decision
      // with marketing consent or bypass Shopify's GPC enforcement.
      privacy.setTrackingConsent(consent, () => {
        updatingConsent = false;
        lastApplied = key;
        // Re-read the CMP: a refusal may have arrived during the API call.
        synchronize();
      });
    } catch {
      updatingConsent = false;
    }
  }

  ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline', 'CookiebotOnLoad', 'CookiebotOnDialogDisplay']
    .forEach((event) => window.addEventListener(event, synchronize));
  document.addEventListener('visitorConsentCollected', loadAffiliates);
  synchronize();
})();
